import { checkName } from "../../utility";
import { ArsText } from "../inputs/ArsText";

/**
 * Props for the NamedObjectControl component.
 */
export interface NamedObjectControlProps {
  /** RFC 6901 JSON Pointer path to the NamedObject in ArsContext state */
  dataPath: string;
  /** Can rename the object (optional) */
  canRename?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Invalid names */
  invalidNames?: string[];
}

/**
 * A component for displaying and editing NamedObject properties (name, description, label).
 *
 * This component provides inputs for the three standard fields that are shared across many
 * ARS entities (AnalysisSet, DataSubset, Group, etc.):
 * - name: The name for the instance (required)
 * - description: A textual description (optional)
 * - label: A short informative description for display (optional)
 *
 * All changes are synchronised with ArsContext state using the PATCH_ARS_STATE action
 * through the ArsText components.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the NamedObject in ArsContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @param props.disabled - Optional flag to disable all inputs.
 * @param props.invalidNames - Optional array of invalid names to validate against.
 * @returns A React functional component rendering the named object editor.
 */
export const NamedObjectControl = ({
  dataPath,
  canRename = false,
  disabled = false,
  invalidNames = [],
}: NamedObjectControlProps): React.ReactElement => {
  return (
    <div>
      <ArsText
        dataPath={`${dataPath}/name`}
        debounceMilliseconds={-1} // Only debounce this on blur
        disabled={!canRename || disabled}
        id={`${dataPath.replace(/\//g, "-")}-name`}
        label="Name"
        labelWidth={2}
        placeholder="Enter name"
        validCondition={checkName}
        validationTargetOptions={{ existingValues: invalidNames }}
      />
      <ArsText
        dataPath={`${dataPath}/label`}
        id={`${dataPath.replace(/\//g, "-")}-label`}
        label="Label"
        labelWidth={2}
        placeholder="Enter label"
        isMultiline={true}
        disabled={disabled}
      />
      <ArsText
        dataPath={`${dataPath}/description`}
        id={`${dataPath.replace(/\//g, "-")}-description`}
        label="Description"
        labelWidth={2}
        type="textarea"
        rows={3}
        placeholder="Enter description"
        allowCarriageReturn={true}
        disabled={disabled}
      />
    </div>
  );
};

NamedObjectControl.displayName = "NamedObjectControl";
